/**
 * System role rows are seeded with a fixed name in the database
 * (`clinic_admin` is always "Clinic admin"; `employee` is "Medarbejder" or
 * "Employee"). The roles screen should follow the UI language instead.
 * A name the clinic has since changed is left as stored.
 */

export type ClinicRoleNameSource = {
  name: string;
  system_key: string | null;
};

type SystemRoleKey = 'clinic_admin' | 'employee';

const SEEDED_SYSTEM_ROLE_NAMES: Record<SystemRoleKey, readonly string[]> = {
  clinic_admin: ['Clinic admin', 'Klinikadministrator'],
  employee: ['Employee', 'Medarbejder'],
};

function isSystemRoleKey(value: string | null): value is SystemRoleKey {
  return value === 'clinic_admin' || value === 'employee';
}

function systemRoleLabelKey(systemKey: SystemRoleKey): string {
  switch (systemKey) {
    case 'clinic_admin':
      return 'roles.system_name_clinic_admin';
    case 'employee':
      return 'roles.system_name_employee';
    default: {
      const unknown: never = systemKey;
      return unknown;
    }
  }
}

function matchesSeededName(systemKey: SystemRoleKey, storedName: string): boolean {
  return SEEDED_SYSTEM_ROLE_NAMES[systemKey].some(
    (seed) => seed.localeCompare(storedName, 'da', { sensitivity: 'accent' }) === 0,
  );
}

export function clinicRoleDisplayName(
  role: ClinicRoleNameSource,
  t: (key: string) => string,
): string {
  if (!isSystemRoleKey(role.system_key)) return role.name;
  const stored = role.name.trim();
  if (!matchesSeededName(role.system_key, stored)) return role.name;
  return t(systemRoleLabelKey(role.system_key));
}

/** Membership or invitation plus the clinic's Roller & rettigheder rows. */
export type StaffRoleAssignment = {
  role: string;
  custom_role_id?: string | null;
};

export type StaffRoleOption = ClinicRoleNameSource & {
  id: string;
};

/**
 * Label for the staff list and the person-card badge.
 * Same name as the Rolle dropdown on the person page: the chosen role
 * from Roller & rettigheder, or the admin / default employee label.
 * Display only — access checks still use `clinic_memberships.role`.
 */
export function staffRoleLabel(
  assignment: StaffRoleAssignment,
  clinicRoles: readonly StaffRoleOption[],
  t: (key: string) => string,
): string {
  if (assignment.role === 'main_clinic_admin') return t('users.role_main_clinic_admin');
  if (assignment.role === 'clinic_admin') return t('users.role_clinic_admin');

  const selected = assignment.custom_role_id
    ? clinicRoles.find((role) => role.id === assignment.custom_role_id)
    : undefined;
  if (selected) return clinicRoleDisplayName(selected, t);

  if (assignment.role === 'employee') {
    const employeeTemplate = clinicRoles.find((role) => role.system_key === 'employee');
    if (employeeTemplate) return clinicRoleDisplayName(employeeTemplate, t);
    return t('users.role_employee');
  }

  return t(`users.role_${assignment.role}`);
}
